import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Stethoscope, Clock, CheckCircle2, FileText, AlertTriangle, Upload } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

// Referral tracker: what's still a draft, what's been sent and is waiting on the specialist, and
// what has come back. "Awaiting reply" is the lane that matters — a referral nobody chased is the
// classic way a patient falls through the cracks.
export default function Referrals({ drafts = [], awaiting = [], replied = [] }) {
  const [openId, setOpenId] = useState(null)
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)

  const submitOutcome = (r) => {
    if (!notes.trim()) { window.alert('Add a short note on what the specialist reported.'); return }
    setBusy(true)
    router.post(r.record_outcome_path, { outcome_notes: notes, report: file },
      { forceFormData: true, onFinish: () => { setBusy(false); setOpenId(null); setNotes(''); setFile(null) } })
  }

  const Row = ({ r, children }) => (
    <div className="border-b border-brand-border/50 px-4 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <Link href={r.path} className="text-sm font-medium text-brand-ink hover:text-brand-primary">{r.ref}</Link>
        {r.urgent && <span className="inline-flex items-center gap-1 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"><AlertTriangle size={10} /> {r.urgency_label}</span>}
        {r.ai_drafted && <span className="rounded border border-brand-border bg-brand-surface px-1.5 py-0.5 text-[11px] text-brand-muted">AI-drafted</span>}
        <Link href={r.patient_file_path} className="text-sm text-brand-ink-soft hover:underline">{r.patient}</Link>
        <span className="text-xs text-brand-muted">→ {r.recipient}</span>
        <span className="ml-auto text-xs text-brand-muted">{children}</span>
      </div>
    </div>
  )

  const Section = ({ title, icon: Icon, tone, items, empty, children }) => (
    <div className="mb-6">
      <h2 className={cn('mb-2 flex items-center gap-2 text-sm font-semibold', tone)}>
        <Icon size={15} /> {title} <span className="text-brand-muted">({items.length})</span>
      </h2>
      <div className="rounded-xl border border-brand-border bg-white">
        {items.length === 0 ? <p className="px-4 py-3 text-xs text-brand-muted">{empty}</p> : children}
      </div>
    </div>
  )

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Stethoscope size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Referrals</h1>
          <p className="text-sm text-brand-muted">Letters to specialists — drafts, awaiting a reply, and outcomes</p>
        </div>
        <Link href="/specialists" className="ml-auto rounded-lg border border-brand-border bg-white px-3 py-2 text-sm font-semibold text-brand-ink-soft hover:bg-brand-surface">
          Specialist directory
        </Link>
      </div>

      <Section title="Awaiting the specialist's reply" icon={Clock} tone="text-amber-700" items={awaiting} empty="Nothing outstanding.">
        {awaiting.map((r) => (
          <div key={r.id}>
            <Row r={r}>
              sent {r.sent_at} · {r.days_since_sent}d ago
              <button onClick={() => { setOpenId(openId === r.id ? null : r.id); setNotes(''); setFile(null) }}
                className="ml-3 rounded border border-brand-primary px-2 py-0.5 text-[11px] font-semibold text-brand-primary hover:bg-brand-surface">
                Record outcome
              </button>
            </Row>
            {openId === r.id && (
              <div className="border-b border-brand-border/50 bg-brand-surface/40 px-4 py-3">
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="What did the specialist report back?"
                  className="w-full rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-brand-border bg-white px-2.5 py-1.5 text-xs text-brand-ink-soft hover:bg-brand-surface">
                    <Upload size={12} /> {file ? file.name : 'Attach their report (optional)'}
                    <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                  <button onClick={() => submitOutcome(r)} disabled={busy}
                    className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-60">
                    {busy ? 'Saving…' : 'Save outcome'}
                  </button>
                  <span className="text-xs text-brand-muted">Their report is filed to the patient's Correspondence folder.</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="Drafts (not sent)" icon={FileText} tone="text-brand-ink" items={drafts} empty="No drafts.">
        {drafts.map((r) => <Row key={r.id} r={r}>draft</Row>)}
      </Section>

      <Section title="Replied" icon={CheckCircle2} tone="text-emerald-700" items={replied} empty="No outcomes recorded yet.">
        {replied.map((r) => (
          <Row key={r.id} r={r}>replied {r.outcome_received_at}</Row>
        ))}
      </Section>
    </DashboardLayout>
  )
}
